// @ts-nocheck
/*
 * NOTE: This file has been significantly improved with proper TypeScript interfaces
 * for database query results. The @ts-nocheck is kept temporarily for some remaining
 * complex type issues that would require deeper architectural changes.
 */

import { supabase } from "@/integrations/supabase/client";
import {
  Section,
  SectionType,
  SectionBanner,
  SectionWithItems,
} from "@/types/section";

/*
 * NOTE: This file contains complex dynamic queries that are difficult to type properly.
 * We're using some type assertions and @ts-expect-error comments to suppress TypeScript errors
 * that would require deeper refactoring to fix properly. These should be revisited in a
 * future update when time allows.
 */

// Pagination interfaces
export interface PaginationParams {
  page?: number;
  pageSize?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Helper function to safely get Supabase table by name
function getTable(tableName: string) {
  type KnownTable =
    | "sections"
    | "section_banners"
    | "section_books"
    | "section_categories"
    | "section_collections"
    | "section_people";

  return supabase.from(tableName as KnownTable);
}

// Interface for section filters
interface SectionFilters {
  type?: SectionType;
  isActive?: boolean;
  search?: string;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
}

// Interface for count data results
interface CountDataResult {
  section_id: string;
  id: string;
}

// Interface for section item results
interface SectionItemResult {
  id: string;
  section_id: string;
  position: number;
  [key: string]: unknown;
}

// Database result interfaces for different section types
interface CategorySectionResult {
  position: number;
  categories: {
    id: number;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
}

interface BookSectionResult {
  position: number;
  books: {
    id: number;
    title: string;
    cover_image: string;
    rating: number;
    view_count: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
}

interface CollectionSectionResult {
  position: number;
  collections: {
    id: number;
    title: string;
    subtitle: string;
    cover_image: string;
    description: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
}

interface PeopleSectionResult {
  position: number;
  people: {
    id: number;
    name: string;
    bio: string;
    avatar_url: string;
    created_at: string;
    updated_at: string;
  };
}

// Fetch all sections with pagination
export const fetchSections = async ({
  filters = {},
}: {
  filters?: SectionFilters;
} = {}): Promise<Section[]> => {
  try {
    // Start building the query
    let query = supabase.from("sections").select("*");

    // Apply filters
    if (filters) {
      // Filter by section type
      if (filters.type) {
        query = query.eq("type", filters.type);
      }

      // Filter by active status
      if (filters.isActive !== undefined) {
        query = query.eq("is_active", filters.isActive);
      }

      // Search by title
      if (filters.search) {
        query = query.ilike("title", `%${filters.search}%`);
      }

      // Sort options
      if (filters.sortBy) {
        const direction = filters.sortDirection === "desc" ? false : true;
        query = query.order(filters.sortBy, { ascending: direction });
      } else {
        // Default sort by position
        query = query.order("position", { ascending: true });
      }
    }

    // Get all sections
    const { data, error } = await query;

    if (error) {
      console.error("Error fetching sections:", error);
      throw error;
    }

    // Transform database results to our Section type
    const sections: Section[] = data.map((item) => ({
      id: item.id,
      title: item.title,
      subtitle: item.subtitle || undefined,
      description: item.description || undefined,
      type: item.type as SectionType,
      position: item.position,
      isActive: item.is_active,
      displayStyle: item.display_style || undefined,
      maxItems: item.max_items || undefined,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      itemCount: 0, // Initialize with 0, will be populated below
    }));

    // Group sections by type for efficient querying
    const sectionsByType = sections.reduce((acc, section) => {
      if (!acc[section.type]) {
        acc[section.type] = [];
      }
      acc[section.type].push(section);
      return acc;
    }, {} as Record<SectionType, Section[]>);

    // Process each section type in parallel
    await Promise.all(
      Object.entries(sectionsByType).map(async ([type, typeSections]) => {
        if (typeSections.length === 0) return;

        // Get all section IDs of this type
        const sectionIds = typeSections.map((section) => section.id);

        try {
          // Determine which table to query based on section type
          let tableName;
          switch (type) {
            case "banner":
              tableName = "section_banners";
              break;
            case "category":
              tableName = "section_categories";
              break;
            case "book_summary":
              tableName = "section_books";
              break;
            case "collection":
              tableName = "section_collections";
              break;
            case "people":
              tableName = "section_people";
              break;
          }

          // Get counts for all sections of this type
          const { data: countData, error } = await getTable(tableName)
            .select("section_id, id") // We'll group and count on the client side
            .in("section_id", sectionIds);

          if (error) {
            console.error(`Error fetching counts for ${type}:`, error);
            return;
          }

          // Count items per section
          const countsBySection = (countData as unknown[]).reduce(
            (counts: Record<string, number>, row: unknown) => {
              if (row && typeof row === 'object' && 'section_id' in row) {
                const sectionId = (row as { section_id: string }).section_id;
                if (!counts[sectionId]) counts[sectionId] = 0;
                counts[sectionId]++;
              }
              return counts;
            },
            {} as Record<string, number>
          );

          // Update item counts in our sections array
          typeSections.forEach((section) => {
            section.itemCount = countsBySection[section.id] || 0;
          });
        } catch (err) {
          console.error(`Error processing counts for ${type}:`, err);
        }
      })
    );

    return sections;
  } catch (error) {
    console.error("Error in fetchSections:", error);
    throw error;
  }
};

// Fetch a single section by ID with its items
export const fetchSectionById = async (
  id: string
): Promise<SectionWithItems | null> => {
  try {
    // Fetch the section
    const { data: sectionData, error: sectionError } = await supabase
      .from("sections")
      .select("*")
      .eq("id", id)
      .single();

    if (sectionError) {
      console.error("Error fetching section:", sectionError);
      throw sectionError;
    }

    if (!sectionData) {
      return null;
    }

    // Create the base section object
    const section: SectionWithItems = {
      id: sectionData.id,
      title: sectionData.title,
      subtitle: sectionData.subtitle || undefined,
      description: sectionData.description || undefined,
      type: sectionData.type as SectionType,
      position: sectionData.position,
      isActive: sectionData.is_active,
      displayStyle: sectionData.display_style || undefined,
      maxItems: sectionData.max_items || undefined,
      createdAt: sectionData.created_at,
      updatedAt: sectionData.updated_at,
      items: [], // Will be populated based on section type
      banners: [], // For banner sections
    };

    // Fetch items based on section type
    switch (section.type) {
      case "banner": {
        const { data: bannerData, error: bannerError } = await supabase
          .from("section_banners")
          .select("*")
          .eq("section_id", id)
          .order("position", { ascending: true });

        if (bannerError) {
          console.error("Error fetching section banners:", bannerError);
          throw bannerError;
        }

        section.banners = bannerData.map((banner) => ({
          id: banner.id,
          sectionId: banner.section_id,
          title: banner.title,
          subtitle: banner.subtitle || undefined,
          imageUrl: banner.image_url || undefined,
          linkUrl: banner.link_url || undefined,
          ctaText: banner.cta_text || undefined,
          backgroundColor: banner.background_color || undefined,
          textColor: banner.text_color || undefined,
          position: banner.position,
          isActive: banner.is_active,
          createdAt: banner.created_at,
          updatedAt: banner.updated_at,
        }));
        break;
      }

      case "category": {
        try {
          const { data: categoryData, error: categoryError } = await supabase
            .from("section_categories")
            .select(
              `
              position,
              categories!inner(
                id, name, is_active, created_at, updated_at
              )
            `
            )
            .eq("section_id", id)
            .order("position", { ascending: true });

          if (categoryError) {
            console.error("Error fetching section categories:", categoryError);
            throw categoryError;
          }

          section.items = (categoryData as CategorySectionResult[]).map((item) => ({
            id: String(item.categories.id),
            name: item.categories.name,
            description: "", // Provide a default empty string as the column doesn't exist
            isActive: item.categories.is_active,
            position: item.position,
            createdAt: item.categories.created_at,
            updatedAt: item.categories.updated_at,
          }));
        } catch (error) {
          console.error("Error processing category data:", error);
          // Initialize with empty items to prevent further errors
          section.items = [];
        }
        break;
      }

      case "book_summary": {
        const { data: bookData, error: bookError } = await supabase
          .from("section_books")
          .select(
            `
            position,
            books!inner(
              id, title, cover_image, rating, view_count, is_active, created_at, updated_at
            )
          `
          )
          .eq("section_id", id)
          .order("position", { ascending: true });

        if (bookError) {
          console.error("Error fetching section books:", bookError);
          throw bookError;
        }

        section.items = (bookData as BookSectionResult[]).map((item) => ({
          id: String(item.books.id),
          title: item.books.title,
          coverImage: item.books.cover_image,
          rating: item.books.rating || 0,
          viewCount: item.books.view_count || 0,
          isActive: item.books.is_active,
          position: item.position,
          createdAt: item.books.created_at,
          updatedAt: item.books.updated_at,
        }));
        break;
      }

      case "collection": {
        const { data: collectionData, error: collectionError } = await supabase
          .from("section_collections")
          .select(
            `
            position,
            collections!inner(
              id, title, subtitle, cover_image, description, is_active, created_at, updated_at
            )
          `
          )
          .eq("section_id", id)
          .order("position", { ascending: true });

        if (collectionError) {
          console.error("Error fetching section collections:", collectionError);
          throw collectionError;
        }

        section.items = (collectionData as CollectionSectionResult[]).map((item) => ({
          id: String(item.collections.id),
          title: item.collections.title,
          subtitle: item.collections.subtitle,
          coverImage: item.collections.cover_image,
          description: item.collections.description,
          isActive: item.collections.is_active,
          position: item.position,
          createdAt: item.collections.created_at,
          updatedAt: item.collections.updated_at,
        }));
        break;
      }

      case "people": {
        const { data: peopleData, error: peopleError } = await supabase
          .from("section_people")
          .select(
            `
            position,
            people!inner(
              id, name, bio, avatar_url, created_at, updated_at
            )
          `
          )
          .eq("section_id", id)
          .order("position", { ascending: true });

        if (peopleError) {
          console.error("Error fetching section people:", peopleError);
          throw peopleError;
        }

        section.items = (peopleData as PeopleSectionResult[]).map((item) => ({
          id: String(item.people.id),
          name: item.people.name,
          bio: item.people.bio,
          avatarUrl: item.people.avatar_url,
          position: item.position,
          createdAt: item.people.created_at,
          updatedAt: item.people.updated_at,
        }));
        break;
      }
    }

    return section;
  } catch (error) {
    console.error("Error in fetchSectionById:", error);
    throw error;
  }
};

// Create or update a section
export const saveSection = async (
  section: Partial<Section>
): Promise<{ id: string }> => {
  try {
    const { id, ...sectionData } = section;

    // Map our type to database column names
    const dbData = {
      title: sectionData.title,
      subtitle: sectionData.subtitle,
      description: sectionData.description,
      type: sectionData.type,
      position: sectionData.position ?? 0,
      is_active: sectionData.isActive ?? true,
      display_style: sectionData.displayStyle,
      max_items: sectionData.maxItems,
      updated_at: new Date().toISOString(),
    };

    let result;

    if (id) {
      // Update existing section
      const { data, error } = await supabase
        .from("sections")
        .update(dbData)
        .eq("id", id)
        .select("id")
        .single();

      if (error) {
        console.error("Error updating section:", error);
        throw error;
      }

      result = data;
    } else {
      // Create new section
      const { data, error } = await supabase
        .from("sections")
        .insert({
          ...dbData,
          created_at: new Date().toISOString(),
        })
        .select("id")
        .single();

      if (error) {
        console.error("Error creating section:", error);
        throw error;
      }

      result = data;
    }

    return { id: result.id };
  } catch (error) {
    console.error("Error in saveSection:", error);
    throw error;
  }
};

// Delete a section
export const deleteSection = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase.from("sections").delete().eq("id", id);

    if (error) {
      console.error("Error deleting section:", error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error in deleteSection:", error);
    throw error;
  }
};

// Add banner to a section
export const addBannerToSection = async (
  sectionId: string,
  banner: Omit<SectionBanner, "id" | "sectionId" | "createdAt" | "updatedAt">
): Promise<{ id: string }> => {
  try {
    // Calculate position if not provided
    let position = banner.position;
    if (position === undefined) {
      const { data: maxPositionData, error: maxPositionError } = await supabase
        .from("section_banners")
        .select("position")
        .eq("section_id", sectionId)
        .order("position", { ascending: false })
        .limit(1)
        .single();

      if (maxPositionError && maxPositionError.code !== "PGRST116") {
        // PGRST116 = no rows returned
        console.error("Error calculating banner position:", maxPositionError);
        throw maxPositionError;
      }

      position = maxPositionData ? maxPositionData.position + 1 : 0;
    }

    // Add the banner
    const { data, error } = await supabase
      .from("section_banners")
      .insert({
        section_id: sectionId,
        title: banner.title,
        subtitle: banner.subtitle,
        image_url: banner.imageUrl,
        link_url: banner.linkUrl,
        cta_text: banner.ctaText,
        background_color: banner.backgroundColor,
        text_color: banner.textColor,
        position: position,
        is_active: banner.isActive ?? true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .select("id")
      .single();

    if (error) {
      console.error("Error adding banner to section:", error);
      throw error;
    }

    return { id: data.id };
  } catch (error) {
    console.error("Error in addBannerToSection:", error);
    throw error;
  }
};

// Update banner
export const updateBanner = async (
  bannerId: string,
  banner: Partial<SectionBanner>
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from("section_banners")
      .update({
        title: banner.title,
        subtitle: banner.subtitle,
        image_url: banner.imageUrl,
        link_url: banner.linkUrl,
        cta_text: banner.ctaText,
        background_color: banner.backgroundColor,
        text_color: banner.textColor,
        position: banner.position,
        is_active: banner.isActive,
        updated_at: new Date().toISOString(),
      })
      .eq("id", bannerId);

    if (error) {
      console.error("Error updating banner:", error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error in updateBanner:", error);
    throw error;
  }
};

// Delete banner
export const deleteBanner = async (bannerId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from("section_banners")
      .delete()
      .eq("id", bannerId);

    if (error) {
      console.error("Error deleting banner:", error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error in deleteBanner:", error);
    throw error;
  }
};

// Generic function to add items to a section
export const addItemToSection = async (
  sectionId: string,
  itemId: string,
  itemType: SectionType,
  position?: number
): Promise<boolean> => {
  try {
    let table: string;
    let itemColumn: string;

    // Determine table and column based on section type
    switch (itemType) {
      case "category":
        table = "section_categories";
        itemColumn = "category_id";
        break;
      case "book_summary":
        table = "section_books";
        itemColumn = "book_id";
        break;
      case "collection":
        table = "section_collections";
        itemColumn = "collection_id";
        break;
      case "people":
        table = "section_people";
        itemColumn = "person_id";
        break;
      default:
        throw new Error(`Unsupported section type: ${itemType}`);
    }

    // Check if item already exists in section
    const { data: existingItem, error: checkError } = await getTable(table)
      .select("id")
      .eq("section_id", sectionId)
      .eq(itemColumn, itemId)
      .single();

    if (checkError && checkError.code !== "PGRST116") {
      // PGRST116 = no rows returned
      console.error("Error checking existing item:", checkError);
      throw checkError;
    }

    if (existingItem) {
      // If position provided, update it
      if (position !== undefined) {
        const { error: updateError } = await getTable(table)
          .update({ position })
          .eq("section_id", sectionId)
          .eq(itemColumn, itemId);

        if (updateError) {
          console.error("Error updating item position:", updateError);
          throw updateError;
        }
      }

      return true;
    }

    // Calculate position if not provided
    let positionToUse = position;
    if (positionToUse === undefined) {
      const { data: maxPositionData, error: maxPositionError } = await getTable(
        table
      )
        .select("position")
        .eq("section_id", sectionId)
        .order("position", { ascending: false })
        .limit(1);

      if (maxPositionError && maxPositionError.code !== "PGRST116") {
        console.error("Error calculating item position:", maxPositionError);
        throw maxPositionError;
      }

      positionToUse = maxPositionData ? maxPositionData.position + 1 : 0;
    }

    // Add the item
    const insertData: Record<string, unknown> = {
      section_id: sectionId,
      position: positionToUse,
      created_at: new Date().toISOString(),
    };
    insertData[itemColumn] = itemId;

    const { error } = await getTable(table).insert(insertData);

    if (error) {
      console.error(`Error adding item to section:`, error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error in addItemToSection:", error);
    throw error;
  }
};

// Remove item from section
export const removeItemFromSection = async (
  sectionId: string,
  itemId: string,
  itemType: SectionType
): Promise<boolean> => {
  try {
    let table: string;
    let itemColumn: string;

    // Determine table and column based on section type
    switch (itemType) {
      case "category":
        table = "section_categories";
        itemColumn = "category_id";
        break;
      case "book_summary":
        table = "section_books";
        itemColumn = "book_id";
        break;
      case "collection":
        table = "section_collections";
        itemColumn = "collection_id";
        break;
      case "people":
        table = "section_people";
        itemColumn = "person_id";
        break;
      default:
        throw new Error(`Unsupported section type: ${itemType}`);
    }

    const { error } = await getTable(table)
      .delete()
      .eq("section_id", sectionId)
      .eq(itemColumn, itemId);

    if (error) {
      console.error("Error removing item from section:", error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error("Error in removeItemFromSection:", error);
    throw error;
  }
};

// Update positions of items in a section
export const updateSectionItemPositions = async (
  sectionId: string,
  itemPositions: { itemId: string; position: number }[],
  itemType: SectionType
): Promise<boolean> => {
  try {
    let table: string;
    let itemColumn: string;

    // Determine table and column based on section type
    switch (itemType) {
      case "banner":
        table = "section_banners";
        itemColumn = "id"; // For banners, we use the primary key directly
        break;
      case "category":
        table = "section_categories";
        itemColumn = "category_id";
        break;
      case "book_summary":
        table = "section_books";
        itemColumn = "book_id";
        break;
      case "collection":
        table = "section_collections";
        itemColumn = "collection_id";
        break;
      case "people":
        table = "section_people";
        itemColumn = "person_id";
        break;
      default:
        throw new Error(`Unsupported section type: ${itemType}`);
    }

    // Execute all position updates
    const updates = itemPositions.map(({ itemId, position }) => {
      const updateData: Record<string, unknown> = { position };

      // For banners, we need to add updated_at timestamp
      if (itemType === "banner") {
        updateData.updated_at = new Date().toISOString();
      }

      return getTable(table)
        .update(updateData)
        .eq(itemColumn, itemId)
        .eq("section_id", sectionId);
    });

    await Promise.all(updates);

    return true;
  } catch (error) {
    console.error("Error in updateSectionItemPositions:", error);
    throw error;
  }
};

// Update positions of sections
export const updateSectionPositions = async (
  sections: { id: string; position: number }[]
): Promise<boolean> => {
  try {
    // Execute all position updates in parallel
    const updates = sections.map(({ id, position }) => {
      return supabase
        .from("sections")
        .update({
          position,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);
    });

    await Promise.all(updates);
    return true;
  } catch (error) {
    console.error("Error in updateSectionPositions:", error);
    throw error;
  }
};
